import {
  Flex,
  CurrencyValue,
  LineChart,
  DisplaySwitch,
  ChartSkeleton
} from '@blockanalitica/ui';
import { useCallback, useMemo, useState } from 'react';
import { useApi } from '@/api';
import { useChartPeriod } from '@/hooks';
import type { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ChartTimeFilter } from '@/components';

export default function HistoricGraphs() {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi<SimpleChartData[]>({
    url: '/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const [activeDisplayOption, setActiveDisplayOption] =
    useState('total_supply_usd');

  const displayOptions = useMemo(
    () => [
      { key: 'total_supply_usd', value: 'Earn' },
      { key: 'total_borrow_usd', value: 'Borrow' },
      { key: 'total_collateral_usd', value: 'Collateral' }
    ],
    []
  );

  const valueFormatter = useCallback(
    (value: string | number) => (
      <CurrencyValue
        value={value}
        options={{
          currency: 'USD'
        }}
      />
    ),
    []
  );

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }

  return (
    <Flex flexDirection="column" gap="xs">
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      <LineChart
        key={activeDisplayOption}
        data={data}
        config={config}
        datasets={[
          {
            xField: 'date',
            yField: activeDisplayOption
          }
        ]}
        valueFormatter={valueFormatter}
        timeFilter={timeFilter}
      />
    </Flex>
  );
}
